import { ITSDBDataRendererPlugin } from '@/shared/Charting/LineChart/lineChart.types.ts'
import dataRendererPluginUtils from '@/shared/Charting/LineChart/plugins/LineChartTSDBDataRendererPlugin/dataRendererPlugin.utils.ts'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import Button from '@/shared/Buttons/Button'
import LineChart from '@/shared/Charting/LineChart'
import PlayIcon from '@/assets/icons/play-line.svg?react'
import PauseIcon from '@/assets/icons/pause-line.svg?react'
import FullScreenIcon from '@/assets/icons/fullscreen.svg?react'
import LineChartDatasetsTagsLine from '@/shared/Charting/LineChart/plugins/LineChartTSDBDataRendererPlugin/LineChartDatasetsTagsLine'
import css from './lineChartTSDBRenderer.module.css'
import cn from '@/utils/cn.ts'
import canvasUtils from '@/shared/Charting/canvas-utils'

const TSDBDataRendererPlugin: () => ITSDBDataRendererPlugin = () => ({
  loading: false,
  live: true,
  chart: null as LineChart | null,
  intervalId: null,
  dataRef: null,
  dataConfig: null,

  init(chart: LineChart) {
    this.chart = chart
    if (!this.live) return

    // 30 fps
    this.intervalId = setInterval(() => {
      if (!this.dataConfig || !this.dataRef) {
        return
      }

      const tsFrame = this.dataConfig.tsTo - this.dataConfig.tsFrom

      const now = Date.now()

      this.chart.zoom.setZoom({ xMin: now - tsFrame, xMax: now }, 'data-plugin')

      this.chart.render()
    }, 33)
  },
  toggleLoadingState(state) {
    this.loading = state
  },
  setDataRef(ref, dataConfig) {
    const booleanParameters = dataConfig.parameters.filter((p) => p.dataType === 'BOOLEAN')

    if (booleanParameters.length) {
      // Булевы сигналы рендерятся под основым графиком
      this.chart.setPluginInnerPaddings('tsdb-data-renderer', {
        left: 0,
        right: 0,
        top: 0,
        bottom: Math.max(booleanParameters.length * 15, 50),
      })
    }

    this.dataRef = ref
    this.dataConfig = dataConfig
    this.chart.render()
  },
  render(ctx: CanvasRenderingContext2D) {
    if (this.dataRef && this.dataConfig) {
      const { datasets, minValue, maxValue } = dataRendererPluginUtils.dataRefAndDataConfigToDatasets(
        this.dataRef,
        this.dataConfig
      )

      const yMin = minValue - (maxValue - minValue) * 0.2
      const yMax = maxValue + (maxValue - minValue) * 0.2

      if (yMax === yMin) {
        this.chart.zoom.setZoom({ yMax: 1, yMin: -1 }, 'data-plugin')
      } else {
        this.chart.zoom.setZoom({ yMax, yMin }, 'data-plugin')
      }

      this.chart.data = {
        ...this.chart.data,
        datasets,
      }
    }

    const data = this.chart.data

    data.datasets
      .filter((d) => d.type === 'numeric')
      .forEach((ds) => {
        ctx.beginPath()

        ds.data.forEach((v, i) => {
          const x = this.chart.getX(v.ts)
          const y = this.chart.getY(v.value)

          if (i === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        })

        if (ds.data.length > 2) {
          // Create gradient fill from line color to transparent
          const maxYPoint = this.chart.canvas.height - this.chart.paddings.bottom - this.chart.innerPadding.bottom

          const chartHeight = maxYPoint - 2

          const gradient = canvasUtils.gradients.makeVerticalGradient(ctx, 0, chartHeight, ds.color)

          // Close the shape to the bottom of the chart for filling
          const lastX = this.chart.getX(ds.data[ds.data.length - 1].ts)
          const firstX = this.chart.getX(ds.data[0].ts)
          ctx.lineTo(lastX, chartHeight) // down to bottom right
          ctx.lineTo(firstX, chartHeight) // across to bottom left
          ctx.closePath()

          // Fill with gradient
          ctx.fillStyle = gradient
          ctx.fill()

          // Redraw line stroke on top
          ctx.beginPath()
          ds.data.forEach((v, i) => {
            const x = this.chart.getX(v.ts)
            const y = this.chart.getY(v.value)
            if (i === 0) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          })
        }

        ctx.strokeStyle = ds.color
        ctx.lineWidth = 1
        ctx.stroke()
        ctx.closePath()
      })

    const booleanDataSets = data.datasets.filter((d) => d.type === 'boolean')

    // Освобождаем место под отображение булевой индикации в случае, если таковая есть
    if (booleanDataSets.length) {
      const maxYPoint = this.chart.canvas.height - this.chart.padding - this.chart.innerPadding.bottom
      ctx.clearRect(
        this.chart.padding + 1,
        maxYPoint + 1,
        this.chart.canvas.width - 2 * this.chart.padding - 2,
        this.chart.innerPadding.bottom - 2
      )
    }

    booleanDataSets.forEach((ds, index) => {
      const startYCoord = this.chart.canvas.height - this.chart.padding - this.chart.innerPadding.bottom
      const marginTop = index * 10 + 10
      const rectangleTopLeftOy = startYCoord + marginTop

      for (let i = 0; i < ds.data.length; ++i) {
        if (ds.data[i].value === 1) {
          const tsStart = ds.data[i].ts
          let tsEnd = this.live ? Date.now() : tsStart + 100

          while (i < ds.data.length && ds.data[i].value === 1) {
            i++
          }

          if (i !== ds.data.length) {
            tsEnd = ds.data[i].ts
          }

          const oXStart = this.chart.getX(tsStart)
          const oXEnd = this.chart.getX(tsEnd)

          ctx.fillStyle = ds.color
          ctx.fillRect(oXStart, rectangleTopLeftOy, oXEnd - oXStart, 5)
        }
      }
    })
  },
  onAfterRender(ctx: CanvasRenderingContext2D) {
    // В случае, если есть булевы сигналы, рисуем линию - разделитель согласно дизайну
    if (this.chart.data.datasets.some((d) => d.type === 'boolean')) {
      const maxYPoint = this.chart.canvas.height - this.chart.paddings.bottom - this.chart.innerPadding.bottom

      ctx.beginPath()
      ctx.moveTo(this.chart.paddings.left, maxYPoint + 3)
      ctx.strokeStyle = '#f0f0f0'
      ctx.lineWidth = 2
      ctx.lineTo(this.chart.canvas.width, maxYPoint + 3)
      ctx.stroke()
      ctx.lineWidth = 1
      ctx.closePath()
    }
  },
  destroy() {
    clearInterval(this.intervalId)
    this.dataRef = null
  },
  renderHeading(props) {
    const dataSetsList = this.chart.data.datasets

    return (
      <>
        {this.loading && (
          <div
            style={{
              position: 'absolute',
              top: `${Math.max(10, this.chart.paddings.top / 2)}px`,
              right: `${this.chart.paddings.right / 2 - 10}px`,
            }}
          >
            <ThreeDotsLoader />
          </div>
        )}
        <div
          className={cn('flex-space-between', css.heading)}
          style={{
            top: `${this.chart.paddings.top + 20 + +props.isFullscreen * 10}px`,
            left: `${this.chart.paddings.left + 10 + +props.isFullscreen * 10}px`,
            width: `calc(100% - ${this.chart.paddings.left + this.chart.paddings.right + 20 + +props.isFullscreen * 10}px)`,
          }}
        >
          <LineChartDatasetsTagsLine
            datasets={dataSetsList}
            onParameterAdd={props.onParameterAdd}
            onParameterRemove={props.onParameterRemove}
          />

          <div className="inline-flex-gap gap8 center">
            <Button
              size="default"
              type="default"
              icon={
                this.chart.zoom.mode === 'manual' ? (
                  <PlayIcon style={{ width: '20px', height: '20px' }} />
                ) : (
                  <PauseIcon style={{ width: '20px', height: '20px' }} />
                )
              }
              buttonType="button"
              onClick={() => this.chart.zoom.switchMode(this.chart.zoom.mode === 'manual' ? 'auto' : 'manual')}
            />
            <Button
              size="default"
              type={props.isFullscreen ? 'primary' : 'default'}
              icon={
                <FullScreenIcon
                  style={{ width: '20px', height: '20px', color: props.isFullscreen ? '#fff' : '#000' }}
                />
              }
              buttonType="button"
              onClick={props.onToggleFullscreen}
              disabled={!props.onToggleFullscreen}
            />
          </div>
        </div>
      </>
    )
  },
})

export default TSDBDataRendererPlugin
